import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Form, message, Space, Table, TableProps } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { SettingUncheckFugitive, Filter, initUncheckFugitive } from "src/entity/settings/fugitive";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { createUncheckFugitive, deleteUncheckFugitive, getUncheckFugitive, updateUncheckFugitive } from "src/services/settings.service";
import { fugitiveStore } from "src/stores/fugitive-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import FugitiveUncheckModal from "./FugitiveUncheckModal";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import VocsModal from "src/components/Modal";
import axios from "axios";
import { canEditTable } from "../PermissionCheck/PermissionCheck";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];

const FugitiveUncheckTable = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [mode, setMode] = useState<"insert" | "update">("insert");
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<Filters>({});
  const [loading, setLoading] = useState(false);
  const { permissions } = useNavigationMenu();
  const pageSize = 10;
  const [current, setCurrent] = useState(1);
  const [form] = Form.useForm();
  const [selectedUncheckId, setSelectedUncheckId] = useState<string>("");
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);

  const uncheckFugitive = fugitiveStore(useShallow((state) => state.uncheckFugitive));
  const totalUncheckFugitive = fugitiveStore(useShallow((state) => state.totalUncheckFugitive));
  const getUncheckFugitive = fugitiveStore(useShallow((state) => state.getUncheckFugitive));

  const filterUncheckFugitive = fugitiveStore(useShallow((state) => state.filterUncheckFugitive));
  const getFilterUncheckFugitive = fugitiveStore(useShallow((state) => state.getFilterUncheckFugitive));

  const onTableChange: TableProps<SettingUncheckFugitive>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setIsDuplicate(false);
    const formValue = form.getFieldsValue();
    if (formValue) {
      form.setFields(
        Object.keys(formValue).map((field) => ({
          name: field,
          errors: [],
        })),
      );
    }
  };

  const handleDelete = async (_id: string) => {
    try {
      setLoading(true);
      const response = await deleteUncheckFugitive(_id);
      if (response.data.code === 200) {
        setLoading(true);
        messageApi.open({
          type: "success",
          content: CommonSuccess.DELETE_SUCCESSFULL,
        });
        await Promise.all([fetchUncheckFugitive(), getFilterUncheckFugitive()]);
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = async (value: SettingUncheckFugitive) => {
    const { _id } = value;
    VocsModal.delete({
      onOk: async () => {
        if (_id) {
          handleDelete(_id);
        }
      },
    });
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = filterUncheckFugitive.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const onOpenModal = (mode: "insert" | "update", data: SettingUncheckFugitive) => {
    setMode(mode);
    setShowModal(true);
    setSelectedUncheckId(data._id);
    form.setFieldsValue(data);
  };

  const columns: TableProps<SettingUncheckFugitive>["columns"] = [
    {
      title: "No.",
      align: "center",
      render: (_, __, index) => <div>{index + 1 + (current - 1) * pageSize}</div>,
    },
    {
      title: "Code",
      dataIndex: "code",
      filters: mapFilter("code"),
    },
    {
      title: "Uncheck",
      dataIndex: "uncheck",
      filters: mapFilter("uncheck"),
    },
    {
      title: "Reason for Exception",
      dataIndex: "reasonForException",
      filters: mapFilter("reasonForException"),
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => {
              onOpenModal("update", record);
            }}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={16} color={componentPalette.blueGrey} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            onClick={() => showDeleteConfirm(value)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={16} color={componentPalette.blueGrey} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchUncheckFugitive = async () => {
    const query = createParamFilter();
    await getUncheckFugitive(query);
  };

  const onSave = async (mode: "insert" | "update", data: SettingUncheckFugitive) => {
    try {
      let response;
      setLoading(true);
      if (mode == "insert") {
        response = await createUncheckFugitive(data);
      } else {
        if (selectedUncheckId) {
          data = {
            ...data,
            _id: selectedUncheckId,
          };
          response = await updateUncheckFugitive(data);
        }
      }
      if (response?.data.code === 200) {
        setLoading(false);
        setShowModal(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
        await Promise.all([fetchUncheckFugitive(), getFilterUncheckFugitive()]);
      } else {
        setLoading(false);
        setIsDuplicate(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setIsDuplicate(true);
        } else {
          setIsDuplicate(false);
        }
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchUncheckFugitive(), getFilterUncheckFugitive()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchUncheckFugitive(), getFilterUncheckFugitive()]);
      setLoading(false);
    };
    fetchData();
  }, [current, filter]);

  return (
    <div>
      {contextHolder}
      {form && (
        <FugitiveUncheckModal
          form={form}
          open={showModal}
          mode={mode}
          loading={loading}
          isDuplicate={isDuplicate}
          onDuplicateChange={(isDup) => {
            setIsDuplicate(isDup);
          }}
          onCloseModal={onCloseModal}
          onSave={(mode, data) => onSave(mode, data)}
        />
      )}
      {canEditTable(permissions) && (
        <div style={{ display: "flex", justifyContent: "end", paddingBottom: "10px" }}>
          <Button
            type="primary"
            size="middle"
            onClick={() => onOpenModal("insert", initUncheckFugitive)}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </div>
      )}
      <Table
        className="w-full"
        columns={columns}
        dataSource={uncheckFugitive}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total: totalUncheckFugitive,
        }}
      />
    </div>
  );
};

export default FugitiveUncheckTable;
